import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, User, Calendar, MapPin, Info, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { ScheduledCase, SurgeonTemplate, CaseCptLine, Laterality, SurgeonFacility, TemplateCptEntry, Assistant } from '@/lib/templates/types';
import { CodingPanel } from './CodingPanel';
import { AssistantNoteBlock } from './AssistantNoteBlock';
import { RemoveAssistantNoteDialog } from './RemoveAssistantNoteDialog';
import { format } from 'date-fns';
import { useState, useMemo, useCallback } from 'react';
import { useDemoMode } from '@/contexts/DemoModeContext';
import type { ClinicalSeverityData } from '@/lib/clinicalSeverityTypes';
import { crossReferenceCptEvidence, type CptEvidenceResult } from '@/lib/cptEvidenceCrossRef';
import {
  buildPreAuthAuthoringAudit,
  type PreAuthAuthoringAudit,
  type PreAuthAuthoringItem,
} from '@/lib/surgeon-console/preAuthAuthoringBrain';
import { isPerfectWorldDemo } from '@/lib/demo/perfectWorldDemo';

interface CodingReviewStepProps {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  laterality: Laterality | null;
  facility: SurgeonFacility | null;
  cptLines: CaseCptLine[];
  renderedNote: string;
  assistantNoteEnabled: boolean;
  selectedAssistant?: Assistant | null;
  onRemoveLine: (code: string) => void;
  onAddLine: (line: CaseCptLine) => void;
  onToggleModifier: (code: string, modifier: string) => void;
  onRemoveAssistantNote: () => void;
  onTemplateUpdated?: () => void;
  caseId?: string;
  payerPlanId?: string | null;
  highComplexity?: boolean;
  onHighComplexityChange?: (val: boolean) => void;
  bmiValue?: number;
  onBmiValueChange?: (val: number) => void;
  clinicalSeverityData?: ClinicalSeverityData | null;
}

export function CodingReviewStep({
  selectedCase,
  template,
  laterality,
  facility,
  cptLines,
  renderedNote,
  assistantNoteEnabled,
  selectedAssistant,
  onRemoveLine,
  onAddLine,
  onToggleModifier,
  onRemoveAssistantNote,
  onTemplateUpdated,
  caseId,
  payerPlanId,
  clinicalSeverityData,
}: CodingReviewStepProps) {
  const [showRemoveAssistant, setShowRemoveAssistant] = useState(false);
  const [evidenceOverrides, setEvidenceOverrides] = useState<Record<string, string>>({});
  const { isDemoMode } = useDemoMode();
  const perfectWorldDemo = isPerfectWorldDemo(isDemoMode, caseId || selectedCase.id);

  // Cross-reference CPT codes against AI clinical extraction
  const evidenceResults = useMemo(() => {
    return crossReferenceCptEvidence(cptLines, clinicalSeverityData, evidenceOverrides);
  }, [cptLines, clinicalSeverityData, evidenceOverrides]);

  const unsupportedCpts = useMemo(() => {
    return evidenceResults.filter(r => r.status === 'unsupported');
  }, [evidenceResults]);

  const packetClinicalText = useMemo(() => {
    const docText = (selectedCase.pre_loaded_docs || [])
      .map(doc => [doc.type, doc.extracted_text].filter(Boolean).join(': '))
      .join('\n\n');
    return [renderedNote, docText].filter(Boolean).join('\n\n');
  }, [renderedNote, selectedCase.pre_loaded_docs]);

  const authoringAudit = useMemo(() => buildPreAuthAuthoringAudit({
    selectedCase,
    template,
    cptLines,
    laterality,
    clinicalText: packetClinicalText,
    clinicalSeverityData,
  }), [selectedCase, template, cptLines, laterality, packetClinicalText, clinicalSeverityData]);

  const handleEvidenceOverride = useCallback((code: string, reason: string) => {
    setEvidenceOverrides(prev => ({ ...prev, [code]: reason }));
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      {/* Left: Review context */}
      <div className="lg:col-span-3 space-y-3">
        {!perfectWorldDemo && <PreAuthAuthoringBrainPanel audit={authoringAudit} />}

        {/* Case Context Header */}
        <Card className="bg-muted/30">
          <CardContent className="p-3">
            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1 font-medium text-foreground">
                <User className="w-3.5 h-3.5" />
                {selectedCase.patient_name}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {selectedCase.scheduled_date ? format(new Date(selectedCase.scheduled_date), 'MMM d, yyyy') : '—'}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {facility?.facility_name || '—'}
              </span>
              <Badge variant="outline" className="text-[10px]">
                {template.template_name}
                {laterality ? ` — ${laterality.charAt(0).toUpperCase() + laterality.slice(1)}` : ''}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Assistant Note Block */}
        {assistantNoteEnabled && (
          <AssistantNoteBlock
            onDelete={() => setShowRemoveAssistant(true)}
            assistant={selectedAssistant}
            laterality={laterality}
            procedureName={template.template_name}
          />
        )}

      </div>

      {/* Right: Coding Panel */}
      <div className="lg:col-span-2">
        <CodingPanel
          cptLines={cptLines}
          template={template}
          commonAddons={template.common_addons as TemplateCptEntry[]}
          onRemoveLine={onRemoveLine}
          onAddLine={onAddLine}
          onToggleModifier={onToggleModifier}
          onTemplateUpdated={onTemplateUpdated}
          surgeonId={template.surgeon_id}
          caseId={caseId}
          payerPlanId={payerPlanId}
          facilityId={selectedCase.facility_id}
          evidenceResults={evidenceResults}
          onEvidenceOverride={handleEvidenceOverride}
        />
      </div>

      {/* Remove Assistant Note Dialog */}
      <RemoveAssistantNoteDialog
        open={showRemoveAssistant}
        onOpenChange={setShowRemoveAssistant}
        templateId={template.id}
        templateName={template.template_name}
        caseId={caseId}
        onCaseRemove={onRemoveAssistantNote}
        onTemplateUpdated={onTemplateUpdated}
      />
    </div>
  );
}

function PreAuthAuthoringBrainPanel({ audit }: { audit: PreAuthAuthoringAudit }) {
  const visibleItems = audit.items.filter(item => item.severity !== 'guidance');
  const statusLabel =
    audit.status === 'ready'
      ? 'Building clean'
      : audit.status === 'blocked'
        ? 'Stopped before draft'
        : 'Needs surgeon fact';

  return (
    <Card className={audit.status === 'blocked' ? 'border-destructive/50 bg-destructive/5' : audit.status === 'needs_surgeon' ? 'border-amber-300 bg-amber-50/50' : 'border-primary/30 bg-primary/5'}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-background border border-border/60">
              <ShieldCheck className={audit.status === 'ready' ? 'w-4 h-4 text-primary' : audit.status === 'blocked' ? 'w-4 h-4 text-destructive' : 'w-4 h-4 text-amber-600'} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">Denial Defender Authoring Brain</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {audit.summary}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="text-[10px]">
            {statusLabel} · {audit.score}/100
          </Badge>
        </div>

        <div className="space-y-2">
          {visibleItems.map(item => (
            <AuthoringItemRow key={item.id} item={item} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function AuthoringItemRow({ item }: { item: PreAuthAuthoringItem }) {
  const isGood = item.severity === 'satisfied';
  const isBlocked = item.severity === 'blocker';
  return (
    <div className="rounded-lg border border-border/60 bg-background/80 p-3">
      <div className="flex items-start gap-2">
        {isGood ? (
          <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        ) : (
          <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${isBlocked ? 'text-destructive' : 'text-amber-600'}`} />
        )}
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs font-semibold text-foreground">{item.title}</p>
            {item.affectedCodes?.map(code => (
              <Badge key={code} variant="secondary" className="text-[9px] h-4 font-mono">{code}</Badge>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">{item.finding}</p>
          {!isGood && (
            <p className="text-[11px] text-foreground leading-relaxed">
              <span className="font-semibold">Prevention:</span> {item.prevention}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
